import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNotifications } from '../hooks/useNotifications';
import { useSettings } from '../contexts/SettingsContext';
import { useSiteTranslation } from '../hooks/useSiteTranslation';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useLandingSections } from '../hooks/useLandingSections';
import EditableSection from '../components/Cms/EditableSection';
import { getFaqQuestion, getFaqAnswer, type ResolvedSectionContent } from '../i18n/localizeContent';

type FormData = {
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  country?: string;
  immigrationType?: string;
};

type FaqItem = {
  _id: string;
  question: string;
  answer: string;
  i18n?: {
    pt?: { question?: string; answer?: string };
    en?: { question?: string; answer?: string };
    es?: { question?: string; answer?: string };
  };
  order: number;
};

const Contact: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const notifications = useNotifications();
  const { settings } = useSettings();
  const { ts, t, i18n } = useSiteTranslation();
  const contact = settings?.site?.contact;
  const geo = settings?.site?.geo;
  const showLocation = geo?.showLocation !== false;
  const lang = i18n.language?.split('-')[0] || 'pt';

  const { sections, refetch } = useLandingSections('contact');
  const [faqItems, setFaqItems] = useState<FaqItem[]>([]);

  useEffect(() => {
    axios
      .get<FaqItem[]>('/faq')
      .then((res) => setFaqItems(res.data))
      .catch(() => setFaqItems([]));
  }, []);

  const heroFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: ts('contact.title1'), title2: ts('contact.title2'), subtitle: t('contact.subtitle'),
      paragraph1: '', paragraph2: '', visionTitle: '', visionText: '', ctaPrimaryLabel: '', ctaSecondaryLabel: '',
      items: []
    }),
    [ts, t]
  );

  const formFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: t('contact.formTitle'), title2: '', subtitle: t('contact.formSubtitle'),
      paragraph1: '', paragraph2: '', visionTitle: '', visionText: '', ctaPrimaryLabel: '', ctaSecondaryLabel: '',
      items: []
    }),
    [t]
  );

  const mapFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: t('contact.mapTitle'), title2: '', subtitle: t('contact.mapSubtitle'),
      paragraph1: '', paragraph2: '', visionTitle: '', visionText: '', ctaPrimaryLabel: '', ctaSecondaryLabel: '',
      items: []
    }),
    [t]
  );

  const schema = useMemo(
    () =>
      yup.object({
        name: yup.string().required(t('contact.validation.nameRequired')).min(2, t('contact.validation.nameMin')),
        email: yup.string().required(t('contact.validation.emailRequired')).email(t('contact.validation.emailInvalid')),
        phone: yup.string().optional(),
        subject: yup.string().required(t('contact.validation.subjectRequired')).min(5, t('contact.validation.subjectMin')),
        message: yup.string().required(t('contact.validation.messageRequired')).min(10, t('contact.validation.messageMin')),
        country: yup.string().optional(),
        immigrationType: yup.string().optional(),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(schema) as any,
  });

  const immigrationTypes = useMemo(
    () =>
      (['work', 'study', 'family', 'investment', 'refugee', 'other'] as const).map((value) => ({
        value,
        label: t(`contact.immigrationTypes.${value}`),
      })),
    [t]
  );

  const countries = useMemo(
    () =>
      (['brazil', 'canada', 'usa', 'australia', 'portugal', 'spain', 'france', 'germany', 'uk', 'italy', 'newZealand', 'other'] as const).map(
        (key) => t(`contact.countriesList.${key}`)
      ),
    [t]
  );

  const contactInfo = useMemo(
    () =>
      [
        contact?.email
          ? { icon: Mail, title: t('contact.email'), details: [contact.email], description: t('contact.response24h') }
          : null,
        contact?.phone
          ? {
              icon: Phone,
              title: t('contact.phone'),
              details: [contact.phone],
              description: contact.businessHours || t('contact.getInTouch'),
            }
          : null,
        contact?.address
          ? { icon: MapPin, title: t('contact.address'), details: [contact.address], description: t('contact.inPerson') }
          : null,
        contact?.businessHours
          ? { icon: Clock, title: t('contact.hours'), details: [contact.businessHours], description: t('contact.checkHolidays') }
          : null,
      ].filter(Boolean) as Array<{ icon: typeof Mail; title: string; details: string[]; description: string }>,
    [contact, t]
  );

  const defaultContactInfo = useMemo(
    () => [
      { icon: Mail, title: t('contact.email'), details: ['contato@europa.solutions'], description: t('contact.response24h') },
      { icon: Phone, title: t('contact.phone'), details: ['+351 927 524 223'], description: t('contact.weekdayHours') },
      {
        icon: MapPin,
        title: t('contact.address'),
        details: ['Rua de António Carlos Vidal, 195, Bloco B, Loja F', '3840-411 Vagos - Portugal'],
        description: t('contact.inPersonVagos'),
      },
      {
        icon: Clock,
        title: t('contact.hours'),
        details: [`${t('contact.weekdays')} ${t('contact.weekdaysTime')}`, `${t('contact.saturday')} ${t('contact.saturdayTime')}`],
        description: t('contact.closedHolidays'),
      },
    ],
    [t]
  );

  const displayContactInfo = contactInfo.length > 0 ? contactInfo : defaultContactInfo;

  const onSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);
      setSubmitStatus('idle');

      await axios.post('/contacts', data);
      
      setSubmitStatus('success');
      setSubmitMessage(t('contact.formSuccess'));
      notifications.success.contactSent();
      reset();
    } catch (error: any) {
      setSubmitStatus('error');
      const errorMessage = error.response?.data?.message || t('contact.formError');
      setSubmitMessage(errorMessage);
      notifications.error.contactSend(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient section-padding">
        <div className="container-custom">
          <EditableSection sectionKey="contact.hero" data={sections['contact.hero']} fallback={heroFallback} onSaved={refetch}>
            {(content) => (
              <div className="max-w-4xl mx-auto text-center">
                <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  {content.title} <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-600">{content.title2}</span>
                </h1>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  {content.subtitle}
                </p>
              </div>
            )}
          </EditableSection>
        </div>
      </section>

      {/* Contact Info */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid-centered md:[--cols:2] lg:[--cols:4] mb-16">
            {displayContactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <div key={index} className="card text-center group hover:-translate-y-2 transition-transform duration-300">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-primary-600 transition-colors duration-300">
                    <Icon className="w-8 h-8 text-primary-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{info.title}</h3>
                  <div className="space-y-2 mb-4">
                    {info.details.map((detail, detailIndex) => (
                      <p key={detailIndex} className="text-gray-600 font-medium">{detail}</p>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500">{info.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <EditableSection sectionKey="contact.form" data={sections['contact.form']} fallback={formFallback} onSaved={refetch}>
              {(content) => (
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">{content.title}</h2>
                  <p className="text-xl text-gray-600">
                    {content.subtitle}
                  </p>
                </div>
              )}
            </EditableSection>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Form */}
              <div className="lg:col-span-2">
                <form onSubmit={handleSubmit(onSubmit)} className="card">
                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0" />
                      <span>{submitMessage}</span>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center space-x-2">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <span>{submitMessage}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Name */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.fullName')}
                      </label>
                      <input
                        type="text"
                        id="name"
                        {...register('name')}
                        className={`input-field ${errors.name ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder={t('contact.namePlaceholder')}
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.email')} *
                      </label>
                      <input
                        type="email"
                        id="email"
                        {...register('email')}
                        className={`input-field ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder={t('contact.emailPlaceholder')}
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Phone */}
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.phoneLabel')}
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        {...register('phone')}
                        className="input-field"
                        placeholder={t('contact.phonePlaceholder')}
                      />
                    </div>

                    {/* Country */}
                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.countryOfOrigin')}
                      </label>
                      <select
                        id="country"
                        {...register('country')}
                        className="input-field"
                      >
                        <option value="">{t('contact.selectCountry')}</option>
                        {countries.map((country) => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Subject */}
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.subject')}
                      </label>
                      <input
                        type="text"
                        id="subject"
                        {...register('subject')}
                        className={`input-field ${errors.subject ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder={t('contact.subjectPlaceholder')}
                      />
                      {errors.subject && (
                        <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
                      )}
                    </div>

                    {/* Immigration Type */}
                    <div>
                      <label htmlFor="immigrationType" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.immigrationTypeLabel')}
                      </label>
                      <select
                        id="immigrationType"
                        {...register('immigrationType')}
                        className="input-field"
                      >
                        <option value="">{t('contact.selectType')}</option>
                        {immigrationTypes.map((type) => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="mb-6">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.message')}
                    </label>
                    <textarea
                      id="message"
                      rows={6}
                      {...register('message')}
                      className={`input-field resize-none ${errors.message ? 'border-red-500 focus:ring-red-500' : ''}`}
                      placeholder={t('contact.messagePlaceholder')}
                    />
                    {errors.message && (
                      <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>{t('common.sending')}</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>{t('contact.sendMessage')}</span>
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* Sidebar */}
              <div className="space-y-8">
                {/* Quick Contact */}
                <div className="card">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('contact.quickContact')}</h3>
                  <div className="space-y-4">
                    <a
                      href={`tel:${(contact?.phone || '+351 927 524 223').replace(/[^\d+]/g, '')}`}
                      className="flex items-center space-x-3 text-gray-600 hover:text-primary-600 transition-colors duration-200"
                    >
                      <Phone className="w-5 h-5" />
                      <span>{contact?.phone || '+351 927 524 223'}</span>
                    </a>
                    <a
                      href={`mailto:${contact?.email || 'contato@europa.solutions'}`}
                      className="flex items-center space-x-3 text-gray-600 hover:text-primary-600 transition-colors duration-200"
                    >
                      <Mail className="w-5 h-5" />
                      <span>{contact?.email || 'contato@europa.solutions'}</span>
                    </a>
                  </div>
                </div>

                {/* FAQ */}
                <div className="card">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('contact.faqSidebarTitle')}</h3>
                  {faqItems.length > 0 ? (
                    <>
                      <div className="space-y-4">
                        {faqItems.slice(0, 3).map((item) => (
                          <div key={item._id}>
                            <h4 className="font-medium text-gray-900 mb-2">{getFaqQuestion(item, lang)}</h4>
                            <p className="text-sm text-gray-600">{getFaqAnswer(item, lang)}</p>
                          </div>
                        ))}
                      </div>
                      <Link to="/faq" className="inline-block mt-4 text-sm font-medium text-primary-600 hover:text-primary-800">
                        {t('faq.title')} →
                      </Link>
                    </>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">{t('contact.faq1q')}</h4>
                        <p className="text-sm text-gray-600">{t('contact.faq1a')}</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">{t('contact.faq2q')}</h4>
                        <p className="text-sm text-gray-600">{t('contact.faq2a')}</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">{t('contact.faq3q')}</h4>
                        <p className="text-sm text-gray-600">{t('contact.faq3a')}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Office Hours */}
                <div className="card">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('contact.officeHoursTitle')}</h3>
                  {contact?.businessHours ? (
                    <div className="space-y-1 text-sm text-gray-700">
                      {contact.businessHours.split('|').map((line, index) => (
                        <p key={index}>{line.trim()}</p>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('contact.weekdays')}</span>
                        <span className="font-medium">{t('contact.weekdaysTime')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('contact.saturday')}</span>
                        <span className="font-medium">{t('contact.saturdayTime')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('contact.sunday')}</span>
                        <span className="font-medium text-red-600">{t('contact.closed')}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      {showLocation && (
        <section className="bg-white">
          <div className="container-custom py-16">
            <EditableSection sectionKey="contact.map" data={sections['contact.map']} fallback={mapFallback} onSaved={refetch}>
              {(content) => (
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">{content.title}</h2>
                  <p className="text-xl text-gray-600">
                    {content.subtitle}
                  </p>
                </div>
              )}
            </EditableSection>

            <div className="rounded-lg overflow-hidden shadow-lg h-96 border border-gray-200">
              <iframe
                title={ts('contact.mapIframeTitle')}
                src={
                  geo?.latitude && geo?.longitude
                    ? `https://www.google.com/maps?q=${encodeURIComponent(`${geo.latitude},${geo.longitude}`)}&z=17&output=embed`
                    : 'https://www.google.com/maps?q=40.55609669174887,-8.681646674998083&z=17&output=embed'
                }
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Contact;
